import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo
} from 'react';
import { ProductType } from 'apiTyped/hooks/notebooks';

type ProductTypeContextType = {
  productType: ProductType;
  setProductType: (type: ProductType) => void;
  isSpirit: boolean;
};

const ProductTypeContext = createContext<ProductTypeContextType | undefined>(
  undefined
);

type ProductTypeProviderProps = {
  children: ReactNode;
  initialProductType?: ProductType;
};

export const ProductTypeProvider = ({
  children,
  initialProductType = ProductType.Wine
}: ProductTypeProviderProps) => {
  const [productType, setProductType] =
    useState<ProductType>(initialProductType);

  const isSpirit = productType === ProductType.Spirit;

  const value = useMemo(
    () => ({ productType, setProductType, isSpirit }),
    [productType, isSpirit]
  );

  return (
    <ProductTypeContext.Provider value={value}>
      {children}
    </ProductTypeContext.Provider>
  );
};

export const useProductType = () => {
  const context = useContext(ProductTypeContext);
  if (context === undefined) {
    throw new Error('useProductType must be used within a ProductTypeProvider');
  }
  return context;
};

// Helper hook to get conditional labels
export const useNetworkLabels = () => {
  const { isSpirit } = useProductType();

  const getOnLabel = useCallback(
    (defaultLabel: string) => (isSpirit ? 'ON Trade' : defaultLabel),
    [isSpirit]
  );

  const getOffLabel = useCallback(
    (defaultLabel: string) => (isSpirit ? 'OFF Trade' : defaultLabel),
    [isSpirit]
  );

  return useMemo(
    () => ({
      onLabel: isSpirit ? 'ON Trade' : undefined,
      offLabel: isSpirit ? 'OFF Trade' : undefined,
      getOnLabel,
      getOffLabel
    }),
    [isSpirit, getOnLabel, getOffLabel]
  );
};
